/**
 * Patch settings – tenant-scoped backend settings with one-time localStorage import.
 */
import React, { useState, useEffect } from 'react';
import { ShieldCheck, CheckCircle, AlertCircle } from 'lucide-react';
import { apiService } from '../services/api';

export const PATCH_AUTO_APPROVAL_STORAGE_KEY = 'uem_patch_auto_approval_settings';
const STORAGE_KEY = PATCH_AUTO_APPROVAL_STORAGE_KEY;
const IMPORT_FLAG = 'uem_patch_settings_imported_v1';

interface PatchAutoApprovalSettings {
  enabled: boolean;
  severityThreshold: 'Critical' | 'Important' | 'Moderate' | 'Low';
  targetGroupId: string;
}

interface TenantPatchSettings {
  approval_required: boolean;
  auto_approve_critical: boolean;
  auto_approve_security: boolean;
  maintenance_window_enabled: boolean;
  reboot_policy_required: boolean;
}

const DEFAULT_LEGACY: PatchAutoApprovalSettings = {
  enabled: false,
  severityThreshold: 'Important',
  targetGroupId: '',
};

const DEFAULT_TENANT: TenantPatchSettings = {
  approval_required: false,
  auto_approve_critical: false,
  auto_approve_security: false,
  maintenance_window_enabled: false,
  reboot_policy_required: false,
};

export function loadPatchAutoApprovalSettings(): PatchAutoApprovalSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_LEGACY };
    const parsed = JSON.parse(raw);
    return {
      enabled: Boolean(parsed.enabled),
      severityThreshold: parsed.severityThreshold || DEFAULT_LEGACY.severityThreshold,
      targetGroupId: parsed.targetGroupId || '',
    };
  } catch {
    return { ...DEFAULT_LEGACY };
  }
}

const PatchSettings: React.FC = () => {
  const [settings, setSettings] = useState<TenantPatchSettings>(DEFAULT_TENANT);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importNote, setImportNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiService.getPatchSettings();
        const data = res?.data || res || {};
        if (cancelled) return;
        setSettings({
          approval_required: Boolean(data.approval_required),
          auto_approve_critical: Boolean(data.auto_approve_critical),
          auto_approve_security: Boolean(data.auto_approve_security),
          maintenance_window_enabled: Boolean(data.maintenance_window_enabled),
          reboot_policy_required: Boolean(data.reboot_policy_required),
        });

        // One-time import from legacy localStorage
        if (!localStorage.getItem(IMPORT_FLAG)) {
          const legacy = loadPatchAutoApprovalSettings();
          if (legacy.enabled) {
            const imported = {
              approval_required: true,
              auto_approve_critical: ['Critical', 'Important', 'Moderate', 'Low'].includes(legacy.severityThreshold),
              auto_approve_security: ['Critical', 'Important'].includes(legacy.severityThreshold),
              maintenance_window_enabled: false,
              reboot_policy_required: false,
            };
            await apiService.updatePatchSettings(imported);
            if (!cancelled) {
              setSettings(imported);
              setImportNote('Imported previous browser auto-approval settings into tenant settings.');
            }
          }
          localStorage.setItem(IMPORT_FLAG, '1');
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (updates: Partial<TenantPatchSettings>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
    setSaved(false);
    setError(null);
  };

  const handleSave = async () => {
    setError(null);
    try {
      const res = await apiService.updatePatchSettings(settings);
      const data = res?.data || settings;
      setSettings({
        approval_required: Boolean(data.approval_required),
        auto_approve_critical: Boolean(data.auto_approve_critical),
        auto_approve_security: Boolean(data.auto_approve_security),
        maintenance_window_enabled: Boolean(data.maintenance_window_enabled),
        reboot_policy_required: Boolean(data.reboot_policy_required),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save settings');
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500 py-6">Loading patch settings…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-2 flex items-center gap-2">
          <ShieldCheck size={20} className="text-blue-600" />
          Patch Tenant Settings
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          Approval enforcement and auto-approve rules apply to patch installs for this tenant.
        </p>
      </div>

      {importNote && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm">
          {importNote}
        </div>
      )}
      {saved && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
          <CheckCircle size={18} />
          Settings saved.
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      <div className="bg-slate-50 rounded-lg border border-slate-200 p-6 space-y-6 max-w-xl">
        {(
          [
            {
              key: 'approval_required' as const,
              label: 'Require approval before install',
              desc: 'When enabled, Declined and Not_Set patches cannot be installed',
            },
            {
              key: 'auto_approve_critical' as const,
              label: 'Auto-approve Critical',
              desc: 'Automatically approve Critical severity patches at install time',
            },
            {
              key: 'auto_approve_security' as const,
              label: 'Auto-approve Security',
              desc: 'Automatically approve security / Critical / Important patches',
            },
            {
              key: 'maintenance_window_enabled' as const,
              label: 'Maintenance window',
              desc: 'Prefer installs during configured maintenance windows (when used)',
            },
            {
              key: 'reboot_policy_required' as const,
              label: 'Reboot policy required',
              desc: 'Require an assigned reboot policy for Pending_Reboot devices',
            },
          ] as const
        ).map((row) => (
          <div key={row.key} className="flex items-center justify-between gap-4">
            <div>
              <label className="font-medium text-slate-800">{row.label}</label>
              <p className="text-sm text-slate-500">{row.desc}</p>
            </div>
            <button
              type="button"
              onClick={() => handleChange({ [row.key]: !settings[row.key] })}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings[row.key] ? 'bg-blue-600' : 'bg-slate-200'
              }`}
              role="switch"
              aria-checked={settings[row.key]}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                  settings[row.key] ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        ))}

        <div className="pt-4">
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Save settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatchSettings;
